import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import apiService from '../services/api';
import HostSpecificAnalytics from '../components/HostSpecificAnalytics';
import '../styles/pages/MonthlyDigest-Detailed.css';

// URL to world map GeoJSON data with country names
const geoUrl = 'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson';

const MonthlyDigestDetailed = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [digestData, setDigestData] = useState(null);
  const [available28DayPeriods, setAvailable28DayPeriods] = useState([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    metrics: true,
    explanations: true,
    novelMutations: false,
    alerts: false,
    hostAnalytics: false
  });
  const [showHostAnalytics, setShowHostAnalytics] = useState(false);

  useEffect(() => {
    loadAvailable28DayPeriods();
  }, []);

  useEffect(() => {
    fetch28DayPeriodData();
  }, [selectedPeriodIndex]);

  const loadAvailable28DayPeriods = async () => {
    try {
      const response = await apiService.request('/monthly-reports/periods-28day/available');
      if (response?.success && response?.periods?.length > 0) {
        setAvailable28DayPeriods(response.periods);
        // Start from the latest period (most recent)
        const latestPeriodIndex = response.periods.length - 1;
        setSelectedPeriodIndex(latestPeriodIndex);
      } else {
        console.warn('No 28-day periods available');
      }
    } catch (err) {
      console.error('Error loading 28-day periods:', err);
    }
  };

  const fetch28DayPeriodData = async () => {
    setLoading(true);
    setError(null);
    try {
      const periodResponse = await apiService.request(`/monthly-reports/periods-28day/${selectedPeriodIndex}`);
      console.log('28-day period response:', periodResponse);

      if (!periodResponse || !periodResponse.success) {
        throw new Error('No period data available');
      }

      const summaryData = {
        metrics: [periodResponse.overview],
        countries_affected: periodResponse.countries_affected,
        top_clades: periodResponse.top_clades,
        host_distribution: periodResponse.host_distribution || [],
        note: periodResponse.note
      };

      setDigestData({
        batch: null,
        summary: summaryData,
        novelMutations: [],
        knownMutations: periodResponse.known_mutations || [],
        temporal: null,
        alerts: [],
        hostDistribution: periodResponse.host_distribution || []
      });
    } catch (err) {
      console.error('Error fetching 28-day period data:', err);
      setError('Unable to load 28-day period report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePrevious28DayPeriod = () => {
    if (selectedPeriodIndex > 0) {
      setSelectedPeriodIndex(selectedPeriodIndex - 1);
    }
  };

  const handleNext28DayPeriod = () => {
    if (selectedPeriodIndex < available28DayPeriods.length - 1) {
      setSelectedPeriodIndex(selectedPeriodIndex + 1);
    }
  };

  const periodLabel = available28DayPeriods[selectedPeriodIndex]?.label || 'Loading...';

  if (loading || available28DayPeriods.length === 0) {
    return (
      <div className="monthly-digest-detailed">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading 28-day period report...</p>
        </div>
      </div>
    );
  }

  if (error && !digestData) {
    return (
      <div className="monthly-digest-detailed">
        <div className="error-container">
          <h2>Unable to Load Report</h2>
          <p>{error}</p>
          <button onClick={fetch28DayPeriodData} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const data = digestData || { summary: null, novelMutations: [], knownMutations: [], alerts: [] };

  return (
    <div className="monthly-digest-detailed">
      {/* Header */}
      <div className="digest-header-detailed">
        <div className="header-banner">
          <div className="header-banner-content">
            <div className="header-content">
              <h1 className="page-title">H5N1 28-Day Period Surveillance Report</h1>
            </div>

            <div className="header-controls">
              {/* Navigation Controls */}
              <button
                onClick={handlePrevious28DayPeriod}
                className="month-nav-btn"
                disabled={selectedPeriodIndex === 0}
                aria-label="Previous 28-day period"
              >
                ← Previous
              </button>
              <span className="month-display">
                {periodLabel}
                <br />
                <small>Period {selectedPeriodIndex + 1} of {available28DayPeriods.length}</small>
              </span>
              <button
                onClick={handleNext28DayPeriod}
                className="month-nav-btn"
                disabled={selectedPeriodIndex >= available28DayPeriods.length - 1}
                aria-label="Next 28-day period"
              >
                Next →
              </button>

              <button
                onClick={fetch28DayPeriodData}
                className="refresh-btn"
                title="Refresh data"
              >
                ↻ Refresh
              </button>

              <button
                onClick={() => setShowHostAnalytics(!showHostAnalytics)}
                className="analytics-btn"
                title="Toggle detailed host-specific analytics"
              >
                {showHostAnalytics ? '▼ Hide Analytics' : '▶ Show Analytics'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Summary Section */}
      <section className="digest-section quick-summary-section">
        <div className="section-header">
          <h2 className="section-title">What Happened in This 28-Day Period?</h2>
          <button
            className="expand-toggle"
            onClick={() => toggleSection('metrics')}
            aria-label="Toggle metrics"
          >
            {expandedSections.metrics ? '−' : '+'}
          </button>
        </div>

        {expandedSections.metrics && (
          <div className="quick-summary-content">
            <p className="summary-intro">
              This report tracks H5N1 bird flu genomic changes detected worldwide.
              We automatically collect and analyze genetic sequences to detect new mutations and assess pandemic risk.
            </p>

            <div className="metrics-grid-detailed">
              <div className="metric-card-detailed">
                <div className="metric-header-detailed">
                  <div className="metric-info">
                    <div className="metric-label">Sequences Analyzed</div>
                    <div className="metric-value">
                      {(() => {
                        const val = data?.summary?.metrics?.[0]?.total_sequences ||
                                   data?.summary?.total_sequences ||
                                   data?.summary?.executive_summary?.total_sequences;
                        if (typeof val === 'number') return val.toLocaleString();
                        if (typeof val === 'string') return parseInt(val).toLocaleString() || val;
                        return val || '0';
                      })()}
                    </div>
                  </div>
                </div>
                <div className="metric-explanation">
                  <strong>What this means:</strong> We analyzed this many genetic sequences from H5N1 samples
                  collected around the world during this month. Each sequence is a complete genetic code.
                </div>
              </div>

              <div className="metric-card-detailed">
                <div className="metric-header-detailed">
                  <div className="metric-info">
                    <div className="metric-label">New Mutations Found</div>
                    <div className="metric-value">
                      {(() => {
                        const val = data?.summary?.metrics?.[0]?.novel_mutations_total ||
                                   data?.summary?.novel_mutations_last_7_days ||
                                   data?.summary?.executive_summary?.novel_mutations_discovered ||
                                   data?.novelMutations?.length ||
                                   0;
                        if (typeof val === 'string') return parseInt(val) || '0';
                        return val || '0';
                      })()}
                    </div>
                  </div>
                </div>
                <div className="metric-explanation">
                  <strong>What this means:</strong> We found these many genetic changes that are completely new
                  and haven't been seen before in H5N1 samples. These need close monitoring.
                </div>
              </div>

              <div className="metric-card-detailed">
                <div className="metric-header-detailed">
                  <div className="metric-info">
                    <div className="metric-label">Countries Affected</div>
                    <div className="metric-value">
                      {(() => {
                        const val = data?.summary?.metrics?.[0]?.countries_unique ||
                                   data?.summary?.countries_affected?.length ||
                                   data?.summary?.top_countries?.length ||
                                   0;
                        if (typeof val === 'string') return parseInt(val) || '0';
                        return val || '0';
                      })()}
                    </div>
                  </div>
                </div>
                <div className="metric-explanation">
                  <strong>What this means:</strong> H5N1 sequences were detected in this many countries.
                  This helps us understand how widespread the virus is globally.
                </div>
              </div>

              <div className="metric-card-detailed">
                <div className="metric-header-detailed">
                  <div className="metric-info">
                    <div className="metric-label">Genetic Variants (Clades)</div>
                    <div className="metric-value">
                      {(() => {
                        const val = data?.summary?.metrics?.[0]?.clades_detected ||
                                   data?.summary?.top_clades?.length ||
                                   0;
                        if (typeof val === 'string') return parseInt(val) || '0';
                        return val || '0';
                      })()}
                    </div>
                  </div>
                </div>
                <div className="metric-explanation">
                  <strong>What this means:</strong> We detected this many distinct genetic lineages of H5N1.
                  Each variant can have slightly different characteristics.
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Host-Specific Analytics Section */}
      {showHostAnalytics && (
        <section className="digest-section analytics-panel-section">
          <HostSpecificAnalytics
            hostname="accipiter_gentilis"
          />
        </section>
      )}

      {/* Educational Explanations */}
      <section className="digest-section education-section">
        <div className="section-header">
          <h2 className="section-title">Understanding the Data</h2>
          <button
            className="expand-toggle"
            onClick={() => toggleSection('explanations')}
            aria-label="Toggle explanations"
          >
            {expandedSections.explanations ? '−' : '+'}
          </button>
        </div>

        {expandedSections.explanations && (
          <div className="education-content">
            <div className="info-box">
              <h3>What are mutations?</h3>
              <p>
                Mutations are small changes in a virus's genetic code. Think of it like typos in a letter -
                most don't matter, but some can change how the virus works. Some mutations might make the
                virus spread easier or affect how well vaccines work against it.
              </p>
            </div>

            <div className="info-box">
              <h3>What does "novel" mean?</h3>
              <p>
                A "novel" mutation is one that's completely new - never been seen before in the global
                scientific databases. These are especially important to watch because scientists haven't yet
                studied how they might affect the virus's behavior.
              </p>
            </div>

            <div className="info-box">
              <h3>What are clades?</h3>
              <p>
                Clades are like family trees of the virus. All H5N1 viruses are related, but they naturally
                split into different branches as they evolve. Each branch (clade) has its own set of genetic
                characteristics. Think of it like different species of birds - they're all birds, but each species has unique traits.
              </p>
            </div>

            <div className="info-box">
              <h3>Why does this matter?</h3>
              <p>
                By tracking these genetic changes in real-time, we can detect early warning signs of concerning
                viral evolution. If a new mutation appears that might make the virus more dangerous or harder to treat,
                we can alert public health officials immediately.
              </p>
            </div>

            <div className="info-box">
              <h3>Data Sources</h3>
              <p>
                All genetic sequences come from GISAID, the world's largest database of influenza genetic data.
                Scientists worldwide voluntarily share their H5N1 sequences, creating a global picture of viral evolution.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Novel Mutations Section */}
      {data?.novelMutations && (
        Array.isArray(data.novelMutations)
          ? data.novelMutations.length > 0
          : data.novelMutations?.novel_mutations?.length > 0
      ) && (
        <section className="digest-section mutations-section">
          <div className="section-header">
            <h2 className="section-title">New Mutations Detected This Month</h2>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('novelMutations')}
              aria-label="Toggle mutations"
            >
              {expandedSections.novelMutations ? '−' : '+'}
            </button>
          </div>

          {expandedSections.novelMutations && (
            <div className="mutations-content">
              <p className="section-description">
                These genetic mutations were not seen before. Scientists will study them to understand
                if they affect how the virus behaves.
              </p>
              <div className="mutations-list-detailed">
                {(Array.isArray(data.novelMutations)
                  ? data.novelMutations
                  : data.novelMutations?.novel_mutations || []
                ).slice(0, 10).map((mutation, idx) => (
                  <div key={idx} className="mutation-item-detailed">
                    <div className="mutation-badge">NEW</div>
                    <div className="mutation-content">
                      <h4 className="mutation-name">{mutation.mutation_name || mutation.name}</h4>
                      <div className="mutation-details-detailed">
                        <span className="detail">
                          <strong>Position:</strong> {mutation.position || 'N/A'}
                          <span className="tooltip">💡 The location of this change in the genetic code</span>
                        </span>
                        <span className="detail">
                          <strong>Type:</strong> {mutation.mutation_type || mutation.type || 'N/A'}
                        </span>
                        <span className="detail">
                          <strong>Found in:</strong> {mutation.sequence_count || mutation.count || '1'} sample(s)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Alerts Section */}
      {data?.alerts && (
        Array.isArray(data.alerts)
          ? data.alerts.length > 0
          : data.alerts?.alerts?.length > 0
      ) && (
        <section className="digest-section alerts-section">
          <div className="section-header">
            <h2 className="section-title">Current Alerts</h2>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('alerts')}
              aria-label="Toggle alerts"
            >
              {expandedSections.alerts ? '−' : '+'}
            </button>
          </div>

          {expandedSections.alerts && (
            <div className="alerts-content">
              <p className="section-description">
                These are important findings that public health officials should be aware of.
              </p>
              <div className="alerts-list-detailed">
                {(Array.isArray(data.alerts)
                  ? data.alerts
                  : data.alerts?.alerts || []
                ).map((alert, idx) => (
                  <div key={idx} className={`alert-item-detailed alert-${alert.alert_level?.toLowerCase() || 'info'}`}>
                    <div className="alert-icon">
                      {alert.alert_level?.toLowerCase() === 'critical' && '[CRITICAL]'}
                      {alert.alert_level?.toLowerCase() === 'high' && '[HIGH]'}
                      {alert.alert_level?.toLowerCase() === 'medium' && '[MEDIUM]'}
                      {!['critical', 'high', 'medium'].includes(alert.alert_level?.toLowerCase()) && '[INFO]'}
                    </div>
                    <div className="alert-body">
                      <h4 className="alert-title">{alert.title || alert.mutation_name}</h4>
                      <p className="alert-description">{alert.description || 'New alert detected in surveillance data'}</p>
                      <div className="alert-meta">
                        <span className="alert-date">
                          Detected: {new Date(alert.detection_date || alert.timestamp).toLocaleDateString()}
                        </span>
                        <span className={`alert-severity alert-${alert.alert_level?.toLowerCase() || 'info'}`}>
                          {alert.alert_level || 'INFO'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Charts Section */}
      <section className="digest-section charts-section">
        <h2 className="section-title">Data Visualization</h2>
        <div className="charts-grid">
          {/* Top Countries Chart */}
          {data?.summary?.countries_affected && data.summary.countries_affected.length > 0 && (
            <div className="chart-container">
              <h3>Top Countries by Sequence Count</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data.summary.countries_affected.slice(0, 8).map(c => ({
                    name: typeof c === 'string' ? c : (c._id || c.name || 'Unknown'),
                    count: c.count || 0
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Clades Pie Chart */}
          {data?.summary?.top_clades && data.summary.top_clades.length > 0 && (
            <div className="chart-container">
              <h3>Genetic Variants (Clades) Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.summary.top_clades.slice(0, 5).map(c => ({
                      name: typeof c === 'string' ? c : (c._id || c.clade || 'Unknown'),
                      value: c.count || 0
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {['#0066cc', '#ff8b00', '#00a8a8', '#de350b', '#f59e0b'].map((color, idx) => (
                      <Cell key={`cell-${idx}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Host Distribution Chart */}
          {data?.hostDistribution && data.hostDistribution.length > 0 && (
            <div className="chart-container">
              <h3>Host Species Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data.hostDistribution.slice(0, 8).map(h => ({
                    name: typeof h === 'string' ? h : (h._id || h.host || 'Unknown'),
                    count: h.count || 0
                  }))}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#10b981" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Known Mutations Chart */}
          {data?.knownMutations && data.knownMutations.length > 0 && (
            <div className="chart-container">
              <h3>Top Known Mutations Detected</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data.knownMutations.slice(0, 10).map(m => ({
                    name: m.mutation_name || 'Unknown',
                    count: m.count || 0
                  }))}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#ef4444" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* Known Mutations Section */}
      {data?.knownMutations && data.knownMutations.length > 0 && (
        <section className="digest-section mutations-section">
          <div className="section-header">
            <h2 className="section-title">Known Mutations Detected</h2>
          </div>
          <p className="section-description">
            These mutations have been previously documented in H5N1 samples. Scientists monitor them to track viral evolution and assess pandemic potential.
          </p>
          <div className="mutations-list-detailed">
            {data.knownMutations.slice(0, 15).map((mutation, idx) => (
              <div key={idx} className="mutation-item-detailed">
                <div className="mutation-badge-known">KNOWN</div>
                <div className="mutation-content">
                  <h4 className="mutation-name">{mutation.mutation_name || mutation.name}</h4>
                  <div className="mutation-details-detailed">
                    <span className="detail">
                      <strong>Found in:</strong> {mutation.count || 0} sample(s)
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Countries Affected - Heatmap */}
      {data?.summary?.countries_affected && data.summary.countries_affected.length > 0 && (
        <section className="digest-section countries-section">
          <h2 className="section-title">Countries with H5N1 Detections</h2>
          <p className="heatmap-description">Geographic distribution of H5N1 detections by country</p>
          <CountriesHeatmap countriesData={data.summary.countries_affected} />
        </section>
      )}

      {/* Top Clades */}
      {data?.summary?.top_clades && data.summary.top_clades.length > 0 && (
        <section className="digest-section clades-section">
          <h2 className="section-title">Most Common Genetic Variants</h2>
          <p className="section-description">
            These are the dominant genetic lineages detected this month. Each represents a distinct evolutionary branch.
          </p>
          <div className="clades-list">
            {data.summary.top_clades.map((clade, idx) => (
              <div key={idx} className="clade-item">
                <span className="clade-number">{idx + 1}</span>
                <span className="clade-name">
                  {typeof clade === 'string' ? clade : clade._id || clade.name || clade.clade || 'Unknown'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Important Note */}
      <section className="digest-section note-section">
        <h2 className="section-title">Important Information</h2>
        <div className="note-content">
          <div className="note-box">
            <h3>What this report shows:</h3>
            <ul>
              <li>Real-time tracking of H5N1 genetic evolution</li>
              <li>Detection of new mutations not previously seen</li>
              <li>Geographic distribution of the virus</li>
              <li>Genetic variants circulating globally</li>
            </ul>
          </div>

          <div className="note-box">
            <h3>Limitations:</h3>
            <ul>
              <li>Only sequences shared with GISAID are included</li>
              <li>Some countries may have limited sequencing capabilities</li>
              <li>Genetic data alone doesn't tell us everything about the virus</li>
              <li>Lab studies are needed to understand what mutations actually do</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="digest-footer-detailed">
        <div className="footer-content">
          <p>
            <strong>Report Period:</strong> {periodLabel}
          </p>
          <p>
            <strong>Last Updated:</strong> {new Date().toLocaleString()}
          </p>
          <p className="footer-note">
            Data is automatically collected from GISAID and processed using computational genomics methods.
            This surveillance system helps detect early warning signs of H5N1 evolution.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Country name mapping for matching different naming conventions
const COUNTRY_NAME_MAP = {
  'united states': 'United States of America',
  'usa': 'United States of America',
  'uk': 'United Kingdom',
  'united kingdom': 'United Kingdom',
  'south korea': 'South Korea',
  'korea': 'South Korea',
  'netherlands': 'Netherlands',
  'uae': 'United Arab Emirates',
  'united arab emirates': 'United Arab Emirates',
  'czech republic': 'Czechia',
  'laos': 'Laos',
  'lao people\'s democratic republic': 'Laos',
  'sri lanka': 'Sri Lanka',
  'new zealand': 'New Zealand',
  'south africa': 'South Africa',
  'saudi arabia': 'Saudi Arabia',
  'united kingdom of great britain and northern ireland': 'United Kingdom'
};

// Heatmap component for geographic visualization
const CountriesHeatmap = ({ countriesData }) => {
  const [geoData, setGeoData] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Build a map of country names to detection counts
  const countryMap = {};
  let maxCount = 1;
  countriesData.forEach(country => {
    let countryName = typeof country === 'string' ? country : country._id || country.name || 'Unknown';
    const countryCount = typeof country === 'object' && country.count ? country.count : 1;

    // Normalize country name
    const normalizedName = countryName.toLowerCase();
    const geoName = COUNTRY_NAME_MAP[normalizedName] || countryName;

    countryMap[geoName.toLowerCase()] = countryCount;
    maxCount = Math.max(maxCount, countryCount);
  });

  useEffect(() => {
    fetch(geoUrl)
      .then(res => res.json())
      .then(data => {
        // Use the fetched data directly - react-simple-maps can handle TopoJSON format
        setGeoData(data);
      })
      .catch(err => console.error('Error loading geo data:', err));
  }, []);

  const getColorByCount = (count) => {
    if (!count || count === 0) return '#e5e7eb'; // light gray for no detections

    // Normalize intensity based on number of isolates: 0 to 1
    // maxCount represents the country with the highest number of isolates detected
    const intensity = Math.min(count / maxCount, 1);

    // Color scale progression from orange to dark red based on isolate count
    // More isolates = more intense red color
    // > 0 isolates: #fb923c (orange)
    // ~33% max: #f87171 (light red)
    // ~67% max: #ef4444 (red)
    // 100% max: #7f1d1d (dark red - most isolates)

    if (intensity < 0.333) {
      // orange to light red
      const t = intensity / 0.333;
      const r = Math.round(251 + (248 - 251) * t);
      const g = Math.round(146 + (113 - 146) * t);
      const b = Math.round(60 + (113 - 60) * t);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity < 0.667) {
      // light red to red
      const t = (intensity - 0.333) / 0.334;
      const r = Math.round(248 + (239 - 248) * t);
      const g = Math.round(113 + (68 - 113) * t);
      const b = Math.round(113 + (68 - 113) * t);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // red to dark red
      const t = (intensity - 0.667) / 0.333;
      const r = Math.round(239 + (127 - 239) * t);
      const g = Math.round(68 + (29 - 68) * t);
      const b = Math.round(68 + (29 - 68) * t);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  return (
    <div className="countries-heatmap-container">
      <div className="map-wrapper">
        <ComposableMap projection="geoMercator">
          <ZoomableGroup center={[0, 20]} zoom={1.2}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo, idx) => {
                  const geoCountryName = geo.properties.ADMIN || geo.properties.name || geo.properties.NAME || '';
                  const countryName = geoCountryName.toLowerCase();
                  const count = countryMap[countryName] || 0;
                  const color = getColorByCount(count);

                  return (
                    <Geography
                      key={`geo-${idx}`}
                      geography={geo}
                      style={{
                        default: {
                          fill: color,
                          stroke: '#d4d4d8',
                          strokeWidth: 0.75,
                          outline: 'none',
                          cursor: count > 0 ? 'pointer' : 'default',
                          transition: 'all 0.2s ease'
                        },
                        hover: count > 0 ? {
                          fill: '#7f1d1d',
                          stroke: '#1f2937',
                          strokeWidth: 1,
                          outline: 'none',
                          cursor: 'pointer',
                          filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.3))'
                        } : {
                          fill: color,
                          stroke: '#d4d4d8',
                          strokeWidth: 0.75,
                          outline: 'none'
                        },
                        pressed: {
                          fill: '#7f1d1d',
                          stroke: '#1f2937',
                          strokeWidth: 1,
                          outline: 'none'
                        }
                      }}
                      onMouseEnter={() => setHoveredCountry({ name: geoCountryName, count })}
                      onMouseLeave={() => setHoveredCountry(null)}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Tooltip */}
      {hoveredCountry && hoveredCountry.count > 0 && (
        <div className="map-tooltip">
          <div className="tooltip-country">{hoveredCountry.name.toUpperCase()}</div>
          <div className="tooltip-count">{hoveredCountry.count} detection{hoveredCountry.count !== 1 ? 's' : ''}</div>
        </div>
      )}

      {/* Legend */}
      <div className="map-legend">
        <span className="legend-title">Number of Isolates Detected:</span>
        <div className="legend-gradient-bar">
          <div className="gradient-bar-container">
            <div className="gradient-bar" style={{
              backgroundImage: 'linear-gradient(to right, #fb923c, #f87171, #ef4444, #7f1d1d)'
            }}></div>
          </div>
          <div className="gradient-labels">
            <span className="label-left">0</span>
            <span className="label-right">{maxCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyDigestDetailed;
